import { getWatermarkedImageUrl } from '@/lib/watermark-image';

export type GameImagePreference = 'thumb' | 'card' | 'detail' | 'hero';

/** Slot image folders that differ from the CMS provider slug. */
const SLOT_PROVIDER_SLUG_MAP: Record<string, string> = {
  'champion-studio': 'champion',
  'holle-games': 'h-lle-games',
};

const GAME_IMAGE_FILENAME = {
  thumb: '160x134_middle.webp',
  card: '320x268_middle.webp',
  detail: '640x536_middle.webp',
  hero: '800x600_middle.webp',
} as const;

const GAME_IMAGE_FALLBACK_ORDER: Record<GameImagePreference, readonly string[]> = {
  thumb: [
    GAME_IMAGE_FILENAME.thumb,
    GAME_IMAGE_FILENAME.card,
    GAME_IMAGE_FILENAME.detail,
    GAME_IMAGE_FILENAME.hero,
  ],
  card: [
    GAME_IMAGE_FILENAME.card,
    GAME_IMAGE_FILENAME.detail,
    GAME_IMAGE_FILENAME.hero,
    GAME_IMAGE_FILENAME.thumb,
  ],
  detail: [
    GAME_IMAGE_FILENAME.detail,
    GAME_IMAGE_FILENAME.hero,
    GAME_IMAGE_FILENAME.card,
    GAME_IMAGE_FILENAME.thumb,
  ],
  hero: [
    GAME_IMAGE_FILENAME.hero,
    GAME_IMAGE_FILENAME.detail,
    GAME_IMAGE_FILENAME.card,
    GAME_IMAGE_FILENAME.thumb,
  ],
};

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => String(value ?? '').trim()).filter(Boolean)));
}

export function getGameImageSlug(providerSlug: string, gameSlug: string): string {
  const normalizedProviderSlug = String(providerSlug ?? '').trim();
  const normalizedGameSlug = String(gameSlug ?? '').trim();

  if (!normalizedProviderSlug || !normalizedGameSlug) {
    return normalizedGameSlug;
  }

  return normalizedGameSlug.startsWith(`${normalizedProviderSlug}-`)
    ? normalizedGameSlug.slice(normalizedProviderSlug.length + 1)
    : normalizedGameSlug;
}

export function getGameImageCandidates(
  providerSlug: string,
  gameSlug: string,
  preference: GameImagePreference = 'card',
): string[] {
  const storageBase = String(process.env.NEXT_PUBLIC_STORAGE_URL ?? '').trim().replace(/\/+$/, '');
  const normalizedProviderSlug = String(providerSlug ?? '').trim();
  const providerSlugForImage = SLOT_PROVIDER_SLUG_MAP[normalizedProviderSlug] ?? normalizedProviderSlug;
  const imageGameSlug = getGameImageSlug(normalizedProviderSlug, gameSlug);

  if (!storageBase || !providerSlugForImage || !imageGameSlug) {
    return [];
  }

  const basePath = `${storageBase}/img/slots/${providerSlugForImage}/${imageGameSlug}`;
  return uniqueStrings(
    GAME_IMAGE_FALLBACK_ORDER[preference].map((filename) =>
      getWatermarkedImageUrl(`${basePath}/${filename}`, 'game'),
    ),
  );
}

export function getPrimaryGameImage(
  providerSlug: string,
  gameSlug: string,
  preference: GameImagePreference = 'card',
): string {
  return getGameImageCandidates(providerSlug, gameSlug, preference)[0] ?? '';
}

/** Direct storage URL for Open Graph / Telegram (no watermark API redirect). */
export function getGameOgImageUrl(providerSlug: string, gameSlug: string): string {
  const storageBase = String(process.env.NEXT_PUBLIC_STORAGE_URL ?? '').trim().replace(/\/+$/, '');
  const normalizedProviderSlug = String(providerSlug ?? '').trim();
  const providerSlugForImage = SLOT_PROVIDER_SLUG_MAP[normalizedProviderSlug] ?? normalizedProviderSlug;
  const imageGameSlug = getGameImageSlug(normalizedProviderSlug, gameSlug);

  if (!storageBase || !providerSlugForImage || !imageGameSlug) {
    return '';
  }

  const basePath = `${storageBase}/img/slots/${providerSlugForImage}/${imageGameSlug}`;
  return `${basePath}/${GAME_IMAGE_FALLBACK_ORDER.hero[0]}`;
}
